import { Button } from '@smiles/voebiz-ui-kit';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { createBeneficiariesBatch, createBeneficiary, getBeneficiariesByCpf } from '../../services/services';
import DrawerModal from '../DrawerModal';
import ErrorModal from '../ErrorModal';
import { Dropzone } from '../Dropzone';
import IncludeBeneficiaryForm from '../IncludeBeneficiaryForm';
import IncludeInstructions from '../IncludeInstructions';
import IncludeModalButtons from '../IncludeModalButtons';
import IncludeOnePersonErrorModal from '../IncludeOnePersonErrorModal';
import IncludeOnePersonSuccessModal from '../IncludeOnePersonSuccessModal';
import LoadedFile from '../LoadedFile';
import Loader from '../Loader';
import OrangeLoading from '../OrangeLoading';
import SuccessModal from '../SuccessModal';

interface Props {
  isOpen: boolean;
  closeModal(): void;
  type: string;
  setTab(tab: string): void;
}

const ModalInclude = ({ isOpen = false, closeModal, type, setTab }: Props) => {
  const { t } = useTranslation(['beneficiaries', 'includeTeam']);

  const [selectedFile, setSelectedFile] = useState<File[]>([]);

  const [isInstructionsActive, setIsInstructionsActive] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [isOrangeLoading, setIsOrangeLoading] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);

  const [showOnePersonSuccessModal, setShowOnePersonSuccessModal] = useState(false);

  const [showOnePersonErrorModal, setShowOnePersonErrorModal] = useState(false);

  const [cpfAlreadyExists, setCpfAlreadyExists] = useState(false);

  const [base64, setBase64] = useState<string | ArrayBuffer | null>();

  const [name, setName] = useState('');

  const [cpfValue, setCpfValue] = useState('');

  const [birthday, setBirthday] = useState<Dayjs | null>(null);

  const [email, setEmail] = useState('');

  const [filename, setFilename] = useState('');

  const [searchParams] = useSearchParams();

  const handleFilesAdded = (files: File[]) => {
    setSelectedFile(files);
  };
  async function sendCSVFile() {
    setIsLoading(true);
    createBeneficiariesBatch(base64 as string)
      .then((response) => {
        if (response.success) {
          setShowSuccessModal(true);
          setFilename(response.data?.filename);
        } else {
          setShowErrorModal(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleClearForm = () => {
    setName('');
    setCpfValue('');
    setBirthday(null);
    setEmail('');
  };

  const handleCpfExist = () => {
    setCpfAlreadyExists(true);
    setIsOrangeLoading(false);
    setShowOnePersonErrorModal(true);
    handleClearForm();
  };

  const checkIfExistsBeneficiary = async (cpfNumber: string) => {
    const response = await getBeneficiariesByCpf(cpfNumber);

    return response.success ?? false;
  };

  const includeOneBeneficiary = async () => {
    setIsOrangeLoading(true);

    setIsLoading(true);

    const cpfNumber = cpfValue.replace(/[.-]/g, '');

    const hasBeneficiary = await checkIfExistsBeneficiary(cpfNumber);

    if (hasBeneficiary) {
      handleCpfExist();
      setIsLoading(false);
      return;
    }

    const payload = {
      name,
      cpf: cpfNumber,
      birthdate: dayjs(birthday).format('MM/DD/YYYY'),
      email,
      status: 'Active',
    };

    createBeneficiary(payload)
      .then((data) => {
        if (!data?.success) {
          setShowOnePersonErrorModal(true);
        } else setShowOnePersonSuccessModal(true);
      })
      .catch(() => {
        setShowOnePersonErrorModal(true);
      })
      .finally(() => {
        setIsOrangeLoading(false);
        setIsLoading(false);
        handleClearForm();
      });
  };

  const getBase64 = (file: File[]) => {
    const reader = new FileReader();
    reader.readAsDataURL(file[0]);
    reader.onload = () => {    
      const base64String = reader.result as string;
    ;
    const base64Csv = base64String.replace(/^data:application\/vnd\.ms-excel(;|,)/, "data:text/csv$1");
      setBase64(base64Csv);
    };
  };
  const handleCloseModal = () => {
    closeModal();
    setSelectedFile([]);
    setIsInstructionsActive(false);
    setShowSuccessModal(false);
    setShowErrorModal(false);
    setShowOnePersonSuccessModal(false);
    setShowOnePersonErrorModal(false);
    searchParams.delete('m');
  };
  const handleOnePersonSuccessModalClick = () => {
    setShowOnePersonSuccessModal(false);
    closeModal();
  };
  const handleOnePersonSuccessModalClickOut = () => {
    setShowOnePersonSuccessModal(false);
  };
  const handleOnePersonErrorModalClick = () => {
    cpfAlreadyExists && closeModal();
    setShowOnePersonErrorModal(false);
  };
  const handleErrorModal = () => {
    setShowErrorModal(false);
    setSelectedFile([]);
  };
  const handleSuccessModal = () => {
    setSelectedFile([]);
    setIsInstructionsActive(false);
    setShowSuccessModal(false);
  };

  const handleBackButtonClick = () => closeModal();
  const handleShowInstructions = () => setIsInstructionsActive(true);
  const handleClearFile = () => setSelectedFile([]);
  const modalValidation =
    !isInstructionsActive &&
    !showSuccessModal &&
    !showErrorModal &&
    !isOrangeLoading &&
    !showOnePersonSuccessModal &&
    !showOnePersonErrorModal;
  const showInstructions = isInstructionsActive && !showSuccessModal && !showErrorModal;

  useEffect(() => {
    if (selectedFile.length !== 0) {
      getBase64(selectedFile);
    }
  }, [selectedFile]);

  const isIncludeOneBeneficiary = type === 'one';

  return (
    <DrawerModal
      hideheader={isOrangeLoading ?? null}
      id="include-beneficiary-modal"
      open={isOpen}
      anchor="right"
      onClose={handleCloseModal}
    >
      <div className={isOrangeLoading ? 'include-modal-container-orange-loading' : 'include-modal-container'}>
        {modalValidation && (
          <>
            <span className="title">{isIncludeOneBeneficiary ? t('include_one') : t('include_many')}</span>
            <div className="tabs">
              <span
                className={isIncludeOneBeneficiary ? 'tab-title-active' : 'tab-title'}
                onClick={() => setTab('one')}
              >
                {t('include_one')}
              </span>
              <span
                className={!isIncludeOneBeneficiary ? 'tab-title-active' : 'tab-title'}
                onClick={() => setTab('many')}
              >
                {t('include_many')}
              </span>
            </div>
            {isIncludeOneBeneficiary ? (
              <IncludeBeneficiaryForm
                handleIncludePersonClick={() => includeOneBeneficiary()}
                handleBackButtonClick={handleBackButtonClick}
                name={name}
                cpfValue={cpfValue}
                email={email}
                birthday={dayjs(birthday).format('MM/DD/YYYY')}
                setName={setName}
                setCpfValue={setCpfValue}
                setBirthday={setBirthday}
                setEmail={setEmail}
              />
            ) : (
              <div data-testid="test-uploader">
                <IncludeModalButtons
                  downloadArchive="arquivoGuiaImportacaoBeneficiarios"
                  title={t('includeTeam:need_help')}
                  handleShowInstructions={handleShowInstructions}
                />
                {selectedFile?.length === 0 ? (
                  <Dropzone
                    extensions={['text/csv']}
                    textExtensions={t('modalInclude:csv_file')}
                    textDesktop={t('modalInclude:select_files')}
                    textMobile={t('modalInclude:select_files_mobile')}
                    cbOnFilesAdded={(files) => handleFilesAdded(files)}
                  />
                ) : (
                  <LoadedFile selectedFile={selectedFile} handleClearFile={handleClearFile} />
                )}
                <Button
                  onClick={() => sendCSVFile()}
                  disabled={selectedFile.length < 1 || isLoading}
                  className="include-button-form"
                  data-testid="test-include-button"
                >
                  {isLoading ? <Loader small /> : 'Incluir pessoas'}
                </Button>
              </div>
            )}
          </>
        )}
        {showInstructions && (
          <div>
            <IncludeInstructions />
            <button type="button" className="back-button" onClick={() => setIsInstructionsActive(false)}>
              {t('includeTeam:go_back')}
            </button>
          </div>
        )}
        {showSuccessModal && <SuccessModal toggle={handleSuccessModal} filename={filename} />}
        {showOnePersonSuccessModal && (
          <IncludeOnePersonSuccessModal
            toggle={handleOnePersonSuccessModalClick}
            toggleOut={handleOnePersonSuccessModalClickOut}
          />
        )}
        {showOnePersonErrorModal && (
          <IncludeOnePersonErrorModal toggle={handleOnePersonErrorModalClick} cpfAlreadyExists={cpfAlreadyExists} />
        )}
        {isOrangeLoading && <OrangeLoading />}
        {showErrorModal && <ErrorModal toggleErrorModal={handleErrorModal} />}
      </div>
    </DrawerModal>
  );
};

export default ModalInclude;
